import React, { useState } from 'react';

import { Button, DropdownButton, Modal, Dropdown as DropdownBS, Alert } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { decode } from 'html-entities';

import { useAppSelector } from 'hooks';
import { AddIcon, ThreeVerticalDotsIcon, TrashcanIcon } from 'assets/icons';
import { useAxios } from 'configs/requests';
import { Dropdown } from 'components/Forms';
import { Error, Loading } from 'components/Fetch';
import { isPatientArchived } from 'store/patient/reducer';

type IActions = 'add' | 'clear';
interface IModalActions {
  type: IActions;
  show: boolean;
}

interface IActionsMenuProps {
  getCarePlan: () => any;
  hasCarePlan?: boolean;
}

const ActionsMenu = ({ getCarePlan, hasCarePlan = false }: IActionsMenuProps) => {
  const intl = useIntl();
  const patient = useAppSelector(({ patient }) => patient?.data);
  const [modal, setModal] = useState<IModalActions>({ type: 'add', show: false });
  const [selectedTemplate, setTemplate] = useState<string>('');
  const _isPatientArchived = useAppSelector(isPatientArchived);
  const [{ data }] = useAxios<{ carePlanTemplates: ICarePlanShort[] }>(
    {
      url: `/clinician/2.0.0/CarePlanTemplate?group_id=${patient?.subgroup_gid}&statuses=1`,
      method: 'GET'
    },
    { useCache: false }
  );

  const carePlanTemplatesOptions =
    data?.carePlanTemplates.map((x) => {
      return {
        label: intl.formatMessage({
          id: decode(x.template_description),
          defaultMessage: decode(x.template_description)
        }),
        value: x.template_id
      };
    }) || [];

  const [{ loading: loadingClear, error: errorClear }, clear] = useAxios<{
    carePlanTemplates: ICarePlanShort[];
  }>(
    {
      url: '/clinician/PatientCarePlanBulk',
      method: 'DELETE',
      data: {
        specificUids: [patient?.uid]
      }
    },
    { manual: true }
  );

  const [{ loading: loadingAdd, error: errorAdd }, add] = useAxios(
    {
      url: '/clinician/PatientCarePlanBulk',
      method: 'PATCH'
    },
    { manual: true }
  );

  const handleModal = (type?: IActions) => {
    setModal({ type: type || modal.type, show: !modal.show });
  };

  const closeModal = () => {
    setModal({ ...modal, show: false });
    setTemplate('');
  };

  const handleClear = () => {
    clear().then(() => {
      getCarePlan();
      closeModal();
    });
  };

  const handleAddTemplate = () => {
    add({
      data: {
        specificUids: [patient?.uid],
        template_id: selectedTemplate,
        ignore_conflicts: 1
      }
    }).then(() => {
      getCarePlan();
      closeModal();
    });
  };

  return (
    <>
      <DropdownButton
        variant="link"
        id="dropdown-basic-button"
        title={<ThreeVerticalDotsIcon className="icon" />}
        align="end"
        className="no-caret"
        data-test="careplan-dropdown"
        disabled={_isPatientArchived}
      >
        <DropdownBS.Item onClick={() => handleModal('add')} data-test="careplan-dropdown-add">
          <AddIcon className="icon pe-2" />
          <span>{intl.formatMessage({ id: 'addFromTemplateKey' })}</span>
        </DropdownBS.Item>
        <DropdownBS.Item onClick={() => handleModal('clear')} data-test="careplan-dropdown-clear">
          <TrashcanIcon className="icon pe-2" />
          <span>{intl.formatMessage({ id: 'clearKey' })}</span>
        </DropdownBS.Item>
      </DropdownButton>

      <Modal show={modal.show} centered data-test="careplan-action-modal">
        {modal.type === 'add' && (
          <>
            <Modal.Header>
              <Modal.Title>{intl.formatMessage({ id: 'addCarePlanFromTemplateKey' })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>{intl.formatMessage({ id: 'addCarePlanSelectKey' })}</p>
              {errorAdd && <Error />}
              {hasCarePlan && (
                <Alert variant="primary" data-test="careplan-add-template-modal-warning">
                  {intl.formatMessage({ id: 'addCarePlanReplacingKey' })}
                </Alert>
              )}
              <Dropdown
                variant="form"
                options={carePlanTemplatesOptions}
                onChange={(e: ISelectOption) => setTemplate(e.value)}
                dataTestId="careplan-add-template-modal-templates-dropdown"
              />
              {loadingAdd && <Loading className="mt-4" />}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="link"
                onClick={closeModal}
                data-test="careplan-add-template-modal-cancel-btn"
              >
                {intl.formatMessage({ id: 'Cancel' })}
              </Button>
              <Button
                onClick={handleAddTemplate}
                disabled={!selectedTemplate}
                data-test="careplan-add-template-modal-submit-btn"
              >
                {intl.formatMessage({ id: 'continueKey' })}
              </Button>
            </Modal.Footer>
          </>
        )}
        {modal.type === 'clear' && (
          <>
            <Modal.Header>
              <Modal.Title>{intl.formatMessage({ id: 'clearCarePlanKey' })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {errorClear && <Error />}
              <Alert variant="warning" className="m-0" data-test="careplan-clear-modal-warning">
                {intl.formatMessage({ id: 'clearCarePlanWarningKey' })}
              </Alert>
              {loadingClear && <Loading className="mt-4" />}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="link"
                onClick={closeModal}
                data-test="careplan-clear-modal-cancel-btn"
              >
                {intl.formatMessage({ id: 'Cancel' })}
              </Button>
              <Button
                variant="danger"
                disabled={loadingClear}
                onClick={handleClear}
                data-test="careplan-clear-modal-submit-btn"
              >
                {intl.formatMessage({ id: 'clearKey' })}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default ActionsMenu;
